import { useEffect, useState } from 'react';
import OneTrustPlaceholder from '../OneTrustPlaceholder';
import { useDisabledFeatureFlags } from 'lib/hooks/useDisabledFeatureFlags';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { getOneTrustActiveGroups, getOneTrustCookieActiveGroups } from 'lib/utils/onetrust-utils';

type Groups = '2' | '3' | '4';

export enum OneTrustDefaultConsentPreference {
  Consent = 'consent',
  NoConsent = 'noconsent',
}

type DefaultConsentPreference = `${OneTrustDefaultConsentPreference}`;

interface IOneTrustFilter {
  filteredComponent: React.ReactNode; // The component OneTrust applies to
  defaultConsentPref?: DefaultConsentPreference;
  optanonGroups: Groups[];
  enablePlaceholder: boolean;
  placeholderFeatureType?:
    | 'video'
    | 'buynow'
    | 'social'
    | 'ratings'
    | 'productlocator'
    | 'retailerlist'; // Determines consent message to display
}

// Max time to wait for OneTrust's SDK to expose its admin-configured default
// group state before falling back to defaultConsentPref.
const MAX_DEFAULT_GROUPS_POLL_ATTEMPTS = 40;
const DEFAULT_GROUPS_POLL_INTERVAL_MS = 50;

function resolveDefaultConsentPreference(
  siteSettingValue?: string,
  fallbackValue: DefaultConsentPreference = OneTrustDefaultConsentPreference.Consent
): DefaultConsentPreference {
  return Object.values(OneTrustDefaultConsentPreference).includes(
    siteSettingValue as OneTrustDefaultConsentPreference
  )
    ? (siteSettingValue as DefaultConsentPreference)
    : fallbackValue;
}

export default function OneTrustFilter({
  filteredComponent,
  defaultConsentPref,
  optanonGroups,
  enablePlaceholder,
  placeholderFeatureType,
}: IOneTrustFilter) {
  const siteSettings = useSiteSettings();
  const siteSettingDefaultConsentPref = siteSettings?.oneTrustDefaultConsentPreference?.value;
  const resolvedDefaultConsentPref = resolveDefaultConsentPreference(
    siteSettingDefaultConsentPref,
    defaultConsentPref
  );
  const defaultPref = resolvedDefaultConsentPref === OneTrustDefaultConsentPreference.NoConsent;
  const [isBlocked, setBlocked] = useState(defaultPref);
  const [readyToMount, setReadyToMount] = useState(false);
  const disabledFeatures = useDisabledFeatureFlags();
  const oneTrustEnabled = !disabledFeatures?.OT;

  useEffect(() => {
    if (!oneTrustEnabled || typeof window === 'undefined') {
      setBlocked(false);
      setReadyToMount(true);
      return;
    }

    let cancelled = false;

    const evaluateAgainst = (activeGroups: string[]) => {
      if (cancelled) return;
      // Compare active optanon groups vs selected groups
      const userActiveGroups = optanonGroups?.filter((group: Groups) =>
        activeGroups.includes(group)
      );
      const groupsMismatch =
        userActiveGroups?.slice().sort().toString() !== optanonGroups.slice().sort().toString();

      setBlocked(groupsMismatch);
      setReadyToMount(true);
    };

    const cookieActiveGroups = getOneTrustCookieActiveGroups();

    if (cookieActiveGroups) {
      // User (or OneTrust) has already recorded a consent choice — trust the cookie.
      evaluateAgainst(cookieActiveGroups);
      return;
    }

    // No consent cookie yet. Rather than assuming a hardcoded default, honor whatever
    // default state OneTrust's admin console has configured for these categories
    // (sliders on/off). The SDK script loads beforeInteractive, so its default groups
    // are usually already available — use them immediately when present.
    const immediateActiveGroups = getOneTrustActiveGroups();
    if (immediateActiveGroups.length > 0) {
      evaluateAgainst(immediateActiveGroups);
      return;
    }

    // Not available yet — render using defaultConsentPref right away to avoid
    // delaying the initial paint, then keep polling briefly to correct the state
    // once OneTrust reports its real default groups.
    setBlocked(defaultPref);
    setReadyToMount(true);

    let attempts = 0;
    const pollForDefaultGroups = () => {
      if (cancelled) return;
      const activeGroups = getOneTrustActiveGroups();
      if (activeGroups.length > 0) {
        evaluateAgainst(activeGroups);
        return;
      }
      if (attempts >= MAX_DEFAULT_GROUPS_POLL_ATTEMPTS) return;
      attempts += 1;
      setTimeout(pollForDefaultGroups, DEFAULT_GROUPS_POLL_INTERVAL_MS);
    };
    pollForDefaultGroups();

    return () => {
      cancelled = true;
    };
  }, [defaultPref, oneTrustEnabled, optanonGroups]);

  if (!oneTrustEnabled) {
    return <></>;
  }

  return (
    <>
      {isBlocked && enablePlaceholder && (
        <OneTrustPlaceholder featureType={placeholderFeatureType} />
      )}
      {isBlocked && !enablePlaceholder && <></>}
      {!isBlocked && readyToMount && filteredComponent}
    </>
  );
}
